import type { AppLocale } from "./types";
import type { LandingCoreStrings, LandingStrings } from "./landingTypes";
import { LANDING_FAQ } from "./landingFaqByLocale";

const en: LandingCoreStrings = {
  pageMetaTitle: "Wedding invitations",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Beautiful wedding invites on the web—instant for guests, simple for you.",
  ctaAppStoreAria: "Download on the App Store",
  footerPrivacy: "Privacy",
  footerTerms: "Terms",
  navHome: "Home",
  legalPrivacyBody:
    "This page is a placeholder for your privacy policy. Replace this content with your final policy before production.",
  legalTermsBody:
    "This page is a placeholder for your terms of use. Replace this content with your final terms before production.",
  footerCopyright: "© {year} Wedy.Me. All rights reserved.",
  footerLanguage: "Language",
  slides: [
    {
      title: "Beautiful invites, simply",
      body: "Create your page in minutes in one simple flow—names, date, and venue.",
      videoAria: "Animation: building your invite in one flow",
    },
    {
      title: "One scroll, all details",
      body: "Background, details, and live preview in one place—no jumping between screens.",
      videoAria: "Animation: scrolling through invite details and preview",
    },
    {
      title: "Link & QR",
      body: "Share anywhere. Guests open your invite instantly; no app required.",
      videoAria: "Animation: sharing link and QR code",
    },
    {
      title: "RSVPs online",
      body: "Guests reply on your page, and you can see who’s coming.",
      videoAria: "Animation: guests submitting RSVP online",
    },
    {
      title: "Send the invite",
      body: "Send one link—guests open it, read it, and smile.",
      videoAria: "Animation: sending the invitation link",
    },
    {
      title: "One link, many languages",
      body: "Save once, and your mini site goes live, localized for every guest.",
      videoAria: "Animation: one site localized for many languages",
    },
  ],
};

const tr: LandingCoreStrings = {
  pageMetaTitle: "Düğün davetiyeleri",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Web’de şık düğün davetiyeleri—misafirler için anında, sizin için kolay.",
  ctaAppStoreAria: "App Store’dan indir",
  footerPrivacy: "Gizlilik",
  footerTerms: "Şartlar",
  navHome: "Ana sayfa",
  legalPrivacyBody:
    "Bu sayfa gizlilik politikası için yer tutucudur. Yayına çıkmadan önce nihai metninizle değiştirin.",
  legalTermsBody:
    "Bu sayfa kullanım şartları için yer tutucudur. Yayına çıkmadan önce nihai metninizle değiştirin.",
  footerCopyright: "© {year} Wedy.Me. Tüm hakları saklıdır.",
  footerLanguage: "Dil",
  slides: [
    {
      title: "Şık davetiyeler, basitçe",
      body: "İsimleri, tarihi ve mekânı tek akışta girerek davet sayfanızı birkaç dakikada hazırlayın.",
      videoAria: "Animasyon: davet sayfasını tek akışta oluşturma",
    },
    {
      title: "Tek kaydırma, tüm ayrıntılar",
      body: "Arka plan, metinler ve canlı önizleme aynı ekranda; başka ekranlara atlamanız gerekmez.",
      videoAria: "Animasyon: ayrıntıları kaydırma ve önizleme",
    },
    {
      title: "Bağlantı ve QR",
      body: "Bağlantıyı veya QR kodu paylaşın: misafirler davetiyeyi anında açar, uygulama indirmeleri gerekmez.",
      videoAria: "Animasyon: bağlantı ve QR paylaşımı",
    },
    {
      title: "Çevrimiçi RSVP",
      body: "Misafirler sayfanızdan yanıt verir; kimlerin geleceğini kolayca görürsünüz.",
      videoAria: "Animasyon: çevrimiçi RSVP",
    },
    {
      title: "Davetiyeyi gönderin",
      body: "Tek bir bağlantı yeter: misafirleriniz açar, okur ve gülümser.",
      videoAria: "Animasyon: davet bağlantısını gönderme",
    },
    {
      title: "Tek bağlantı, birçok dil",
      body: "Bir kez kaydedin: mini siteniz yayına girer ve her misafir kendi dilinde görür.",
      videoAria: "Animasyon: çok dilli tek site",
    },
  ],
};

const ar: LandingCoreStrings = {
  pageMetaTitle: "دعوات الزفاف",
  heroTitle: "Wedy.Me",
  heroSubtitle: "دعوات زفاف جميلة على الويب—فورية للضيوف، بسيطة لك.",
  ctaAppStoreAria: "التنزيل من App Store",
  footerPrivacy: "الخصوصية",
  footerTerms: "الشروط",
  navHome: "الرئيسية",
  legalPrivacyBody:
    "هذه الصفحة مؤقتة لسياسة الخصوصية. استبدل المحتوى بسياسة نهائية قبل الإطلاق.",
  legalTermsBody: "هذه الصفحة مؤقتة لشروط الاستخدام. استبدل المحتوى بشروط نهائية قبل الإطلاق.",
  footerCopyright: "© {year} Wedy.Me. جميع الحقوق محفوظة.",
  footerLanguage: "اللغة",
  slides: [
    {
      title: "دعوات رائعة، ببساطة",
      body: "أنشئ صفحة الدعوة في دقائق ضمن تدفق واحد: الأسماء والتاريخ والمكان.",
      videoAria: "رسوم متحركة: بناء صفحة الدعوة",
    },
    {
      title: "تمرير واحد، كل التفاصيل",
      body: "الخلفية والمعلومات والمعاينة المباشرة في مكان واحد، دون الانتقال بين الشاشات.",
      videoAria: "رسوم متحركة: التفاصيل والمعاينة",
    },
    {
      title: "الرابط ورمز QR",
      body: "شارك الرابط أو الرمز من أي مكان. يفتح الضيوف الدعوة فوراً ودون تطبيق.",
      videoAria: "رسوم متحركة: مشاركة الرابط ورمز QR",
    },
    {
      title: "تأكيد الحضور عبر الإنترنت",
      body: "الضيوف يردون من صفحتك، ويمكنك رؤية من سيحضر بسهولة.",
      videoAria: "رسوم متحركة: تأكيد الحضور عبر الإنترنت",
    },
    {
      title: "أرسل الدعوة",
      body: "يُكفي رابط واحد: يفتح الضيوف الدعوة، يقرؤونها ويبتسمون.",
      videoAria: "رسوم متحركة: إرسال رابط الدعوة",
    },
    {
      title: "رابط واحد، لغات كثيرة",
      body: "احفظ مرة واحدة فيصبح موقعك المصغر جاهزاً، بلغة كل ضيف.",
      videoAria: "رسوم متحركة: موقع بلغات متعددة",
    },
  ],
};

const de: LandingCoreStrings = {
  pageMetaTitle: "Hochzeitseinladungen",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Schöne Hochzeitseinladungen im Web—sofort für Gäste, einfach für dich.",
  ctaAppStoreAria: "Im App Store laden",
  footerPrivacy: "Datenschutz",
  footerTerms: "AGB",
  navHome: "Start",
  legalPrivacyBody:
    "Diese Seite ist ein Platzhalter für Ihre Datenschutzerklärung. Ersetzen Sie den Text vor dem Livegang.",
  legalTermsBody:
    "Diese Seite ist ein Platzhalter für Ihre Nutzungsbedingungen. Ersetzen Sie den Text vor dem Livegang.",
  footerCopyright: "© {year} Wedy.Me. Alle Rechte vorbehalten.",
  footerLanguage: "Sprache",
  slides: [
    {
      title: "Schöne Einladungen, ganz einfach",
      body: "Erstelle deine Seite in wenigen Minuten in einem Ablauf: Namen, Datum und Ort.",
      videoAria: "Animation: Einladungsseite in einem Ablauf",
    },
    {
      title: "Ein Scrollen, alle Infos",
      body: "Hintergrund, Angaben und Live-Vorschau an einem Ort—ohne zwischen Bildschirmen zu wechseln.",
      videoAria: "Animation: Details scrollen und Vorschau",
    },
    {
      title: "Link & QR",
      body: "Überall teilen. Gäste öffnen die Einladung sofort, ganz ohne App.",
      videoAria: "Animation: Link und QR-Code teilen",
    },
    {
      title: "RSVPs online",
      body: "Gäste antworten auf deiner Seite, und du siehst auf einen Blick, wer kommt.",
      videoAria: "Animation: Online-RSVP",
    },
    {
      title: "Einladung senden",
      body: "Ein Link genügt—Gäste öffnen ihn, lesen und lächeln.",
      videoAria: "Animation: Einladungslink senden",
    },
    {
      title: "Ein Link, viele Sprachen",
      body: "Einmal speichern—und deine Mini-Site ist live, für jeden Gast in der passenden Sprache.",
      videoAria: "Animation: Mehrsprachige Mini-Site",
    },
  ],
};

const fr: LandingCoreStrings = {
  pageMetaTitle: "Invitations de mariage",
  heroTitle: "Wedy.Me",
  heroSubtitle: "De belles invitations de mariage sur le web—instantanées pour les invités, simples pour vous.",
  ctaAppStoreAria: "Télécharger sur l’App Store",
  footerPrivacy: "Confidentialité",
  footerTerms: "Conditions",
  navHome: "Accueil",
  legalPrivacyBody:
    "Cette page est un espace réservé pour votre politique de confidentialité. Remplacez le texte avant la mise en production.",
  legalTermsBody:
    "Cette page est un espace réservé pour vos conditions d’utilisation. Remplacez le texte avant la mise en production.",
  footerCopyright: "© {year} Wedy.Me. Tous droits réservés.",
  footerLanguage: "Langue",
  slides: [
    {
      title: "De belles invitations, tout simplement",
      body: "Créez votre page en quelques minutes dans un seul flux : noms, date et lieu.",
      videoAria: "Animation : créer la page d’invitation",
    },
    {
      title: "Un défilement, tous les détails",
      body: "Arrière-plan, textes et aperçu en direct au même endroit, sans changer d’écran.",
      videoAria: "Animation : défilement et aperçu",
    },
    {
      title: "Lien et QR",
      body: "Partagez le lien ou le code partout. Les invités ouvrent l’invitation tout de suite, sans application.",
      videoAria: "Animation : partage du lien et du QR",
    },
    {
      title: "RSVP en ligne",
      body: "Les invités répondent sur votre page, et vous voyez facilement qui vient.",
      videoAria: "Animation : RSVP en ligne",
    },
    {
      title: "Envoyer l’invitation",
      body: "Un seul lien suffit : vos invités ouvrent, lisent et sourient.",
      videoAria: "Animation : envoi du lien d’invitation",
    },
    {
      title: "Un lien, plusieurs langues",
      body: "Enregistrez une fois : votre mini-site est en ligne, dans la langue de chaque invité.",
      videoAria: "Animation : site multilingue",
    },
  ],
};

const es: LandingCoreStrings = {
  pageMetaTitle: "Invitaciones de boda",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Invitaciones de boda hermosas en la web—al instante para tus invitados, simples para ti.",
  ctaAppStoreAria: "Descargar en el App Store",
  footerPrivacy: "Privacidad",
  footerTerms: "Términos",
  navHome: "Inicio",
  legalPrivacyBody:
    "Esta página es un marcador de posición para tu política de privacidad. Reemplázala con el texto final antes de publicar.",
  legalTermsBody:
    "Esta página es un marcador de posición para tus términos de uso. Reemplázala con el texto final antes de publicar.",
  footerCopyright: "© {year} Wedy.Me. Todos los derechos reservados.",
  footerLanguage: "Idioma",
  slides: [
    {
      title: "Invitaciones hermosas, sencillas",
      body: "Crea tu página en minutos en un solo flujo: nombres, fecha y lugar.",
      videoAria: "Animación: crear tu invitación en un flujo",
    },
    {
      title: "Un scroll, todos los detalles",
      body: "Fondo, textos y vista previa en vivo en el mismo lugar, sin saltar entre pantallas.",
      videoAria: "Animación: detalles y vista previa",
    },
    {
      title: "Enlace y QR",
      body: "Comparte el enlace o el código donde quieras. Tus invitados abren al instante, sin descargar una app.",
      videoAria: "Animación: compartir enlace y QR",
    },
    {
      title: "RSVP en línea",
      body: "Tus invitados responden en tu página y ves con claridad quién asistirá.",
      videoAria: "Animación: RSVP en línea",
    },
    {
      title: "Envía la invitación",
      body: "Con un solo enlace basta: abren, leen y sonríen.",
      videoAria: "Animación: enviar el enlace",
    },
    {
      title: "Un enlace, muchos idiomas",
      body: "Guarda una vez y tu mini sitio queda en línea, en el idioma de cada invitado.",
      videoAria: "Animación: sitio en varios idiomas",
    },
  ],
};

const it: LandingCoreStrings = {
  pageMetaTitle: "Inviti di nozze",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Bellissimi inviti di nozze sul web—istantanei per gli ospiti, semplici per te.",
  ctaAppStoreAria: "Scarica dall’App Store",
  footerPrivacy: "Privacy",
  footerTerms: "Termini",
  navHome: "Home",
  legalPrivacyBody:
    "Questa pagina è un segnaposto per l’informativa sulla privacy. Sostituisci il testo prima della pubblicazione.",
  legalTermsBody:
    "Questa pagina è un segnaposto per i termini d’uso. Sostituisci il testo prima della pubblicazione.",
  footerCopyright: "© {year} Wedy.Me. Tutti i diritti riservati.",
  footerLanguage: "Lingua",
  slides: [
    {
      title: "Inviti bellissimi, in modo semplice",
      body: "Crea la tua pagina in pochi minuti in un unico flusso: nomi, data e luogo.",
      videoAria: "Animazione: creare l’invito in un flusso",
    },
    {
      title: "Uno scroll, tutti i dettagli",
      body: "Sfondo, testi e anteprima live nello stesso schermo, senza passare da una schermata all’altra.",
      videoAria: "Animazione: dettagli e anteprima",
    },
    {
      title: "Link e QR",
      body: "Condividi il link o il codice ovunque. Gli ospiti aprono l’invito subito, senza app.",
      videoAria: "Animazione: condivisione link e QR",
    },
    {
      title: "RSVP online",
      body: "Gli ospiti rispondono sulla tua pagina e tu vedi con facilità chi verrà.",
      videoAria: "Animazione: RSVP online",
    },
    {
      title: "Invia l’invito",
      body: "Basta un link: gli ospiti lo aprono, lo leggono e sorridono.",
      videoAria: "Animazione: invio del link",
    },
    {
      title: "Un link, molte lingue",
      body: "Salva una volta e il tuo mini sito va online, nella lingua di ogni ospite.",
      videoAria: "Animazione: sito multilingue",
    },
  ],
};

const ptPT: LandingCoreStrings = {
  pageMetaTitle: "Convites de casamento",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Convites de casamento lindos na web—na hora para os convidados, simples para si.",
  ctaAppStoreAria: "Transferir na App Store",
  footerPrivacy: "Privacidade",
  footerTerms: "Termos",
  navHome: "Início",
  legalPrivacyBody:
    "Esta página é um espaço reservado para a sua política de privacidade. Substitua o texto antes de publicar.",
  legalTermsBody:
    "Esta página é um espaço reservado para os termos de utilização. Substitua o texto antes de publicar.",
  footerCopyright: "© {year} Wedy.Me. Todos os direitos reservados.",
  footerLanguage: "Idioma",
  slides: [
    {
      title: "Convites lindos, com simplicidade",
      body: "Monte a sua página em minutos num só fluxo: nomes, data e local.",
      videoAria: "Animação: criar o convite num fluxo",
    },
    {
      title: "Um scroll, todos os detalhes",
      body: "Fundo, textos e pré-visualização ao vivo no mesmo ecrã, sem saltar entre ecrãs.",
      videoAria: "Animação: detalhes e pré-visualização",
    },
    {
      title: "Ligação e QR",
      body: "Partilhe a ligação ou o código onde quiser. Os convidados abrem na hora, sem precisar de aplicação.",
      videoAria: "Animação: partilhar ligação e QR",
    },
    {
      title: "RSVP online",
      body: "Os convidados respondem na sua página e pode ver com facilidade quem vem.",
      videoAria: "Animação: RSVP online",
    },
    {
      title: "Envie o convite",
      body: "Uma ligação basta: abrem, leem e sorriem.",
      videoAria: "Animação: enviar a ligação",
    },
    {
      title: "Uma ligação, muitos idiomas",
      body: "Guarde uma vez e o seu mini site fica online, no idioma de cada convidado.",
      videoAria: "Animação: site em vários idiomas",
    },
  ],
};

const ru: LandingCoreStrings = {
  pageMetaTitle: "Свадебные приглашения",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Красивые свадебные приглашения в вебе—мгновенно для гостей, просто для вас.",
  ctaAppStoreAria: "Загрузить в App Store",
  footerPrivacy: "Конфиденциальность",
  footerTerms: "Условия",
  navHome: "Главная",
  legalPrivacyBody:
    "Эта страница — заглушка для политики конфиденциальности. Замените текст перед публикацией.",
  legalTermsBody:
    "Эта страница — заглушка для условий использования. Замените текст перед публикацией.",
  footerCopyright: "© {year} Wedy.Me. Все права защищены.",
  footerLanguage: "Язык",
  slides: [
    {
      title: "Красивые приглашения — просто",
      body: "Создайте страницу за считанные минуты в одном потоке: имена, дата и место.",
      videoAria: "Анимация: создание приглашения",
    },
    {
      title: "Один скролл — все детали",
      body: "Фон, тексты и предпросмотр на одном экране — без переключения между окнами.",
      videoAria: "Анимация: детали и предпросмотр",
    },
    {
      title: "Ссылка и QR",
      body: "Делитесь ссылкой или кодом где угодно. Гости открывают приглашение сразу, без приложения.",
      videoAria: "Анимация: ссылка и QR",
    },
    {
      title: "Онлайн-ответы",
      body: "Гости отвечают на вашей странице, а вы сразу видите, кто придёт.",
      videoAria: "Анимация: онлайн RSVP",
    },
    {
      title: "Отправьте приглашение",
      body: "Достаточно одной ссылки: гости открывают, читают и улыбаются.",
      videoAria: "Анимация: отправка ссылки",
    },
    {
      title: "Одна ссылка — много языков",
      body: "Сохраните один раз — мини-сайт выходит в эфир на языке каждого гостя.",
      videoAria: "Анимация: многоязычный сайт",
    },
  ],
};

const ja: LandingCoreStrings = {
  pageMetaTitle: "ウェディング招待状",
  heroTitle: "Wedy.Me",
  heroSubtitle: "ウェブ上の美しいウェディング招待状—ゲストにはすぐに、あなたにはかんたんに。",
  ctaAppStoreAria: "App Store からダウンロード",
  footerPrivacy: "プライバシー",
  footerTerms: "利用規約",
  navHome: "ホーム",
  legalPrivacyBody:
    "このページはプライバシーポリシー用のプレースホルダーです。公開前に正式な内容に差し替えてください。",
  legalTermsBody:
    "このページは利用規約用のプレースホルダーです。公開前に正式な内容に差し替えてください。",
  footerCopyright: "© {year} Wedy.Me. All rights reserved.",
  footerLanguage: "言語",
  slides: [
    {
      title: "美しい招待状を、シンプルに",
      body: "お名前・日付・会場をひと続きの流れで入力し、数分で招待ページが完成します。",
      videoAria: "アニメーション：招待ページの作成",
    },
    {
      title: "スクロールひとつで、すべての詳細",
      body: "背景・本文・ライブプレビューが同じ画面にあり、別画面に飛ぶ必要はありません。",
      videoAria: "アニメーション：詳細とプレビュー",
    },
    {
      title: "リンクと QR",
      body: "リンクまたは QR を共有すれば、ゲストは招待をすぐに開け、アプリのインストールは不要です。",
      videoAria: "アニメーション：リンクと QR の共有",
    },
    {
      title: "オンラインで RSVP",
      body: "ゲストはページから返信し、誰が来るかをすぐに確認できます。",
      videoAria: "アニメーション：オンライン RSVP",
    },
    {
      title: "招待を送る",
      body: "送るのはリンクひとつ。ゲストが開き、読み、微笑んでもらえます。",
      videoAria: "アニメーション：招待リンクの送信",
    },
    {
      title: "ひとつのリンク、多言語",
      body: "一度保存するだけでミニサイトが公開され、ゲストそれぞれの言語で表示されます。",
      videoAria: "アニメーション：多言語ミニサイト",
    },
  ],
};

const zhHans: LandingCoreStrings = {
  pageMetaTitle: "婚礼请柬",
  heroTitle: "Wedy.Me",
  heroSubtitle: "精美的网页婚礼请柬—宾客即开即用，您轻松创建。",
  ctaAppStoreAria: "在 App Store 下载",
  footerPrivacy: "隐私",
  footerTerms: "条款",
  navHome: "首页",
  legalPrivacyBody: "本页为隐私政策占位内容，正式上线前请替换为正式文本。",
  legalTermsBody: "本页为使用条款占位内容，正式上线前请替换为正式文本。",
  footerCopyright: "© {year} Wedy.Me. 保留所有权利。",
  footerLanguage: "语言",
  slides: [
    {
      title: "精美请柬，简单创建",
      body: "按姓名、日期、场地的单一流程操作，几分钟内即可完成页面。",
      videoAria: "动画：创建请柬页面",
    },
    {
      title: "一滑到底，信息尽览",
      body: "背景、文案与实时预览在同一界面，不必在多个屏幕间跳转。",
      videoAria: "动画：滚动浏览与预览",
    },
    {
      title: "链接与二维码",
      body: "分享链接或二维码即可；宾客即时打开请柬，无需下载应用。",
      videoAria: "动画：分享链接与二维码",
    },
    {
      title: "在线回复邀请",
      body: "宾客在页面上回复，您可以清楚看到谁会出席。",
      videoAria: "动画：在线 RSVP",
    },
    {
      title: "发送请柬",
      body: "只需一条链接：对方打开、阅读、会心一笑。",
      videoAria: "动画：发送邀请链接",
    },
    {
      title: "一个链接，多种语言",
      body: "保存一次，小网站即可上线，并以每位宾客的语言呈现。",
      videoAria: "动画：多语言迷你网站",
    },
  ],
};

const hi: LandingCoreStrings = {
  pageMetaTitle: "शादी के निमंत्रण",
  heroTitle: "Wedy.Me",
  heroSubtitle: "वेब पर खूबसूरत शादी के निमंत्रण—मेहमानों के लिए तुरंत, आपके लिए आसान।",
  ctaAppStoreAria: "App Store पर डाउनलोड करें",
  footerPrivacy: "गोपनीयता",
  footerTerms: "शर्तें",
  navHome: "होम",
  legalPrivacyBody:
    "यह पृष्ठ गोपनीयता नीति के लिए अस्थायी है। लॉन्च से पहले अंतिम पाठ से बदलें।",
  legalTermsBody: "यह पृष्ठ उपयोग की शर्तों के लिए अस्थायी है। लॉन्च से पहले अंतिम पाठ से बदलें।",
  footerCopyright: "© {year} Wedy.Me. सर्वाधिकार सुरक्षित।",
  footerLanguage: "भाषा",
  slides: [
    {
      title: "खूबसूरत निमंत्रण, आसानी से",
      body: "नाम, तारीख और स्थान एक ही क्रम में भरकर कुछ ही मिनटों में पेज तैयार करें।",
      videoAria: "एनीमेशन: निमंत्रण पेज बनाना",
    },
    {
      title: "एक स्क्रॉल, सारे विवरण",
      body: "पृष्ठभूमि, टेक्स्ट और लाइव पूर्वावलोकन एक ही स्क्रीन पर, बिना इधर-उधर जाए।",
      videoAria: "एनीमेशन: विवरण और पूर्वावलोकन",
    },
    {
      title: "लिंक और QR",
      body: "लिंक या QR साझा करें; मेहमान तुरंत निमंत्रण खोल सकते हैं, ऐप की ज़रूरत नहीं।",
      videoAria: "एनीमेशन: लिंक और QR साझा करना",
    },
    {
      title: "ऑनलाइन RSVP",
      body: "मेहमान आपके पेज पर जवाब देते हैं और आप आसानी से देख सकते हैं कौन आ रहा है।",
      videoAria: "एनीमेशन: ऑनलाइन RSVP",
    },
    {
      title: "निमंत्रण भेजें",
      body: "बस एक लिंक भेजें—मेहमान खोलते हैं, पढ़ते हैं और मुस्कुराते हैं।",
      videoAria: "एनीमेशन: निमंत्रण लिंक भेजना",
    },
    {
      title: "एक लिंक, कई भाषाएँ",
      body: "एक बार सहेजें और आपकी छोटी साइट लाइव हो जाए, हर मेहमान अपनी भाषा में देखे।",
      videoAria: "एनीमेशन: बहुभाषी साइट",
    },
  ],
};

const id: LandingCoreStrings = {
  pageMetaTitle: "Undangan pernikahan",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Undangan pernikahan indah di web—langsung untuk tamu, mudah untuk Anda.",
  ctaAppStoreAria: "Unduh di App Store",
  footerPrivacy: "Privasi",
  footerTerms: "Syarat",
  navHome: "Beranda",
  legalPrivacyBody:
    "Halaman ini adalah placeholder untuk kebijakan privasi. Ganti dengan teks final sebelum produksi.",
  legalTermsBody:
    "Halaman ini adalah placeholder untuk syarat penggunaan. Ganti dengan teks final sebelum produksi.",
  footerCopyright: "© {year} Wedy.Me. Hak cipta dilindungi.",
  footerLanguage: "Bahasa",
  slides: [
    {
      title: "Undangan indah, dengan mudah",
      body: "Isi nama, tanggal, dan tempat dalam satu alur—halaman Anda siap dalam beberapa menit.",
      videoAria: "Animasi: membuat halaman undangan",
    },
    {
      title: "Satu scroll, semua detail",
      body: "Latar, teks, dan pratinjau langsung dalam satu layar, tanpa melompat antarlayar.",
      videoAria: "Animasi: detail dan pratinjau",
    },
    {
      title: "Tautan & QR",
      body: "Bagikan tautan atau kode QR; tamu membuka undangan seketika tanpa mengunduh aplikasi.",
      videoAria: "Animasi: berbagi tautan dan QR",
    },
    {
      title: "RSVP online",
      body: "Tamu membalas di halaman Anda dan Anda dengan mudah melihat siapa yang hadir.",
      videoAria: "Animasi: RSVP online",
    },
    {
      title: "Kirim undangan",
      body: "Satu tautan sudah cukup—tamu membuka, membaca, dan tersenyum.",
      videoAria: "Animasi: mengirim tautan undangan",
    },
    {
      title: "Satu tautan, banyak bahasa",
      body: "Simpan sekali—mini situs Anda tayang dalam bahasa setiap tamu.",
      videoAria: "Animasi: situs multibahasa",
    },
  ],
};

const nl: LandingCoreStrings = {
  pageMetaTitle: "Trouwuitnodigingen",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Prachtige trouwuitnodigingen op het web—direct voor gasten, eenvoudig voor jou.",
  ctaAppStoreAria: "Download in de App Store",
  footerPrivacy: "Privacy",
  footerTerms: "Voorwaarden",
  navHome: "Home",
  legalPrivacyBody:
    "Deze pagina is een tijdelijke plek voor je privacybeleid. Vervang de tekst vóór livegang.",
  legalTermsBody:
    "Deze pagina is een tijdelijke plek voor je gebruiksvoorwaarden. Vervang de tekst vóór livegang.",
  footerCopyright: "© {year} Wedy.Me. Alle rechten voorbehouden.",
  footerLanguage: "Taal",
  slides: [
    {
      title: "Mooie uitnodigingen, eenvoudig",
      body: "Vul naam, datum en locatie in één stroom in; je pagina staat binnen enkele minuten.",
      videoAria: "Animatie: uitnodigingspagina maken",
    },
    {
      title: "Één scroll, alle details",
      body: "Achtergrond, tekst en live voorbeeld op hetzelfde scherm, zonder tussen schermen te springen.",
      videoAria: "Animatie: details en voorbeeld",
    },
    {
      title: "Link & QR",
      body: "Deel je link of code overal. Gasten openen de uitnodiging direct, zonder app.",
      videoAria: "Animatie: link en QR delen",
    },
    {
      title: "Online RSVP’s",
      body: "Gasten antwoorden op je pagina en jij ziet in één oogopslag wie er komt.",
      videoAria: "Animatie: online RSVP",
    },
    {
      title: "Stuur de uitnodiging",
      body: "Eén link is genoeg: gasten openen, lezen en glimlachen.",
      videoAria: "Animatie: uitnodigingslink versturen",
    },
    {
      title: "Eén link, veel talen",
      body: "Sla één keer op—je mini-site gaat live in de taal van elke gast.",
      videoAria: "Animatie: meertalige mini-site",
    },
  ],
};

const da: LandingCoreStrings = {
  pageMetaTitle: "Bryllupsinvitationer",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Smukke bryllupsinvitationer på nettet—straks for gæsterne, enkelt for dig.",
  ctaAppStoreAria: "Download på App Store",
  footerPrivacy: "Privatliv",
  footerTerms: "Vilkår",
  navHome: "Hjem",
  legalPrivacyBody:
    "Denne side er en pladsholder til din privatlivspolitik. Erstat teksten før du går live.",
  legalTermsBody:
    "Denne side er en pladsholder til dine brugervilkår. Erstat teksten før du går live.",
  footerCopyright: "© {year} Wedy.Me. Alle rettigheder forbeholdes.",
  footerLanguage: "Sprog",
  slides: [
    {
      title: "Smukke invitationer, enkelt",
      body: "Udfyld navn, dato og sted i ét forløb—din side er klar på få minutter.",
      videoAria: "Animation: opret invitationsside",
    },
    {
      title: "Ét scroll, alle detaljer",
      body: "Baggrund, tekster og live forhåndsvisning på samme skærm uden at skifte.",
      videoAria: "Animation: detaljer og forhåndsvisning",
    },
    {
      title: "Link og QR",
      body: "Del linket eller koden hvor som helst. Gæster åbner med det samme—ingen app nødvendig.",
      videoAria: "Animation: del link og QR",
    },
    {
      title: "Online tilmelding",
      body: "Gæster svarer på din side, og du ser let hvem der kommer.",
      videoAria: "Animation: online tilmelding",
    },
    {
      title: "Send invitationen",
      body: "Ét link er nok—gæster åbner, læser og smiler.",
      videoAria: "Animation: send invitationslink",
    },
    {
      title: "Ét link, mange sprog",
      body: "Gem én gang—din miniside går live på hver gæsts sprog.",
      videoAria: "Animation: flersproget miniside",
    },
  ],
};

const he: LandingCoreStrings = {
  pageMetaTitle: "הזמנות לחתונה",
  heroTitle: "Wedy.Me",
  heroSubtitle: "הזמנות חתונה יפות באינטרנט—מיידי לאורחים, פשוט בשבילכם.",
  ctaAppStoreAria: "הורידו ב‑App Store",
  footerPrivacy: "פרטיות",
  footerTerms: "תנאים",
  navHome: "בית",
  legalPrivacyBody:
    "דף זה הוא מצב זמני למדיניות הפרטיות. החליפו את הטקסט לפני עלייה לאוויר.",
  legalTermsBody: "דף זה הוא מצב זמני לתנאי השימוש. החליפו את הטקסט לפני עלייה לאוויר.",
  footerCopyright: "© {year} Wedy.Me. כל הזכויות שמורות.",
  footerLanguage: "שפה",
  slides: [
    {
      title: "הזמנות יפות, בפשטות",
      body: "מלאו שם, תאריך ומקום בזרימה אחת—העמוד מוכן תוך דקות.",
      videoAria: "אנימציה: יצירת עמוד הזמנה",
    },
    {
      title: "גלילה אחת, כל הפרטים",
      body: "רקע, טקסטים ותצוגה חיה באותו מסך—בלי לקפוץ בין מסכים.",
      videoAria: "אנימציה: פרטים ותצוגה מקדימה",
    },
    {
      title: "קישור ו‑QR",
      body: "שתפו קישור או קוד בכל מקום. האורחים נכנסים מיד—בלי להוריד אפליקציה.",
      videoAria: "אנימציה: שיתוף קישור ו‑QR",
    },
    {
      title: "אישורי הגעה מקוונים",
      body: "האורחים משיבים בעמוד שלכם ואתם רואים בקלות מי מגיע.",
      videoAria: "אנימציה: אישורי הגעה מקוונים",
    },
    {
      title: "שלחו את ההזמנה",
      body: "קישור אחד מספיק—פותחים, קוראים ומחייכים.",
      videoAria: "אנימציה: שליחת קישור הזמנה",
    },
    {
      title: "קישור אחד, שפות רבות",
      body: "שמרו פעם אחת והאתר הקטן עלה—בשפה של כל אורח.",
      videoAria: "אנימציה: אתר רב־לשוני",
    },
  ],
};

const no: LandingCoreStrings = {
  pageMetaTitle: "Bryllupsinvitasjoner",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Vakre bryllupsinvitasjoner på nett—direkte for gjestene, enkelt for deg.",
  ctaAppStoreAria: "Last ned på App Store",
  footerPrivacy: "Personvern",
  footerTerms: "Vilkår",
  navHome: "Hjem",
  legalPrivacyBody:
    "Denne siden er en plassholder for personvernreglene dine. Bytt ut teksten før du lanserer.",
  legalTermsBody:
    "Denne siden er en plassholder for bruksvilkårene dine. Bytt ut teksten før du lanserer.",
  footerCopyright: "© {year} Wedy.Me. Alle rettigheter forbeholdt.",
  footerLanguage: "Språk",
  slides: [
    {
      title: "Vakre invitasjoner, enkelt",
      body: "Fyll inn navn, dato og sted i én flyt—siden din er klar på noen minutter.",
      videoAria: "Animasjon: lag invitasjonsside",
    },
    {
      title: "Én rulling, alle detaljer",
      body: "Bakgrunn, tekster og forhåndsvisning på samme skjerm uten å hoppe rundt.",
      videoAria: "Animasjon: detaljer og forhåndsvisning",
    },
    {
      title: "Lenke og QR",
      body: "Del lenken eller koden hvor du vil. Gjestene åpner med én gang—ingen app trengs.",
      videoAria: "Animasjon: del lenke og QR",
    },
    {
      title: "Nettbaserte svar",
      body: "Gjestene svarer på siden din og du ser enkelt hvem som kommer.",
      videoAria: "Animasjon: nettbaserte svar",
    },
    {
      title: "Send invitasjonen",
      body: "Én lenke holder—gjestene åpner, leser og smiler.",
      videoAria: "Animasjon: send invitasjonslenke",
    },
    {
      title: "Én lenke, mange språk",
      body: "Lagre én gang—minisiden går live på hver gjest sitt språk.",
      videoAria: "Animasjon: flerspråklig miniside",
    },
  ],
};

const sk: LandingCoreStrings = {
  pageMetaTitle: "Svadobné pozvánky",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Krásne svadobné pozvánky na webe—okamžite pre hostí, jednoducho pre vás.",
  ctaAppStoreAria: "Stiahnuť v App Store",
  footerPrivacy: "Súkromie",
  footerTerms: "Podmienky",
  navHome: "Domov",
  legalPrivacyBody:
    "Táto stránka je zástupný text pre zásady ochrany osobných údajov. Nahraďte ho pred spustením.",
  legalTermsBody:
    "Táto stránka je zástupný text pre podmienky používania. Nahraďte ho pred spustením.",
  footerCopyright: "© {year} Wedy.Me. Všetky práva vyhradené.",
  footerLanguage: "Jazyk",
  slides: [
    {
      title: "Krásne pozvánky, jednoducho",
      body: "Vyplňte meno, dátum a miesto v jednom toku—stránka je hotová za pár minút.",
      videoAria: "Animácia: vytvorenie pozvánky",
    },
    {
      title: "Jedno rolovanie, všetky detaily",
      body: "Pozadie, texty a živý náhľad na jednej obrazovke bez skákania medzi obrazovkami.",
      videoAria: "Animácia: detaily a náhľad",
    },
    {
      title: "Odkaz a QR",
      body: "Zdieľajte odkaz alebo kód kdekoľvek. Hostia otvoria hneď—bez inštalácie aplikácie.",
      videoAria: "Animácia: zdieľanie odkazu a QR",
    },
    {
      title: "Online odpovede",
      body: "Hostia odpovedajú na vašej stránke a vy ľahko uvidíte, kto príde.",
      videoAria: "Animácia: online odpovede",
    },
    {
      title: "Odoslanie pozvánky",
      body: "Stačí jeden odkaz—otvoria, prečítajú a usmejú sa.",
      videoAria: "Animácia: odoslanie odkazu",
    },
    {
      title: "Jeden odkaz, viac jazykov",
      body: "Uložte raz—minisite je online v jazyku každého hosťa.",
      videoAria: "Animácia: viacjazyčný minisite",
    },
  ],
};

const uk: LandingCoreStrings = {
  pageMetaTitle: "Весільні запрошення",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Гарні весільні запрошення в інтернеті—миттєво для гостей, просто для вас.",
  ctaAppStoreAria: "Завантажити в App Store",
  footerPrivacy: "Конфіденційність",
  footerTerms: "Умови",
  navHome: "Головна",
  legalPrivacyBody:
    "Ця сторінка — заглушка для політики конфіденційності. Замініть текст перед запуском.",
  legalTermsBody: "Ця сторінка — заглушка для умов використання. Замініть текст перед запуском.",
  footerCopyright: "© {year} Wedy.Me. Усі права захищено.",
  footerLanguage: "Мова",
  slides: [
    {
      title: "Гарні запрошення, просто",
      body: "Заповніть імена, дату й місце в одному потоці—сторінка готова за кілька хвилин.",
      videoAria: "Анімація: створення сторінки запрошення",
    },
    {
      title: "Один скрол — усі деталі",
      body: "Фон, тексти й попередній перегляд на одному екрані без перемикань.",
      videoAria: "Анімація: деталі та перегляд",
    },
    {
      title: "Посилання та QR",
      body: "Поділіться посиланням або кодом будь-де. Гості відкривають одразу — без застосунку.",
      videoAria: "Анімація: поширення посилання та QR",
    },
    {
      title: "Онлайн-відповіді",
      body: "Гості відповідають на вашій сторінці, а ви бачите, хто прийде.",
      videoAria: "Анімація: онлайн-відповіді",
    },
    {
      title: "Надішліть запрошення",
      body: "Достатньо одного посилання—відкривають, читають і посміхаються.",
      videoAria: "Анімація: надсилання посилання",
    },
    {
      title: "Одне посилання — багато мов",
      body: "Збережіть один раз—мінісайт з’являється мовою кожного гостя.",
      videoAria: "Анімація: багатомовний мінісайт",
    },
  ],
};

const vi: LandingCoreStrings = {
  pageMetaTitle: "Thiệp cưới",
  heroTitle: "Wedy.Me",
  heroSubtitle: "Thiệp cưới đẹp trên web—khách mở ngay, bạn tạo dễ dàng.",
  ctaAppStoreAria: "Tải trên App Store",
  footerPrivacy: "Quyền riêng tư",
  footerTerms: "Điều khoản",
  navHome: "Trang chủ",
  legalPrivacyBody:
    "Trang này là nội dung tạm cho chính sách quyền riêng tư. Thay bằng bản chính thức trước khi ra mắt.",
  legalTermsBody:
    "Trang này là nội dung tạm cho điều khoản sử dụng. Thay bằng bản chính thức trước khi ra mắt.",
  footerCopyright: "© {year} Wedy.Me. Bảo lưu mọi quyền.",
  footerLanguage: "Ngôn ngữ",
  slides: [
    {
      title: "Thiệp đẹp, thật đơn giản",
      body: "Nhập tên, ngày và địa điểm theo một luồng duy nhất—trang của bạn sẵn sàng trong vài phút.",
      videoAria: "Hoạt ảnh: tạo trang thiệp mời",
    },
    {
      title: "Một lần cuộn, đủ chi tiết",
      body: "Nền, nội dung và xem trực tiếp trên cùng một màn hình, không phải chuyển qua lại.",
      videoAria: "Hoạt ảnh: chi tiết và xem trước",
    },
    {
      title: "Liên kết & QR",
      body: "Chia sẻ liên kết hoặc mã QR ở bất kỳ đâu. Khách mở thiệp ngay, không cần cài ứng dụng.",
      videoAria: "Hoạt ảnh: chia sẻ liên kết và QR",
    },
    {
      title: "RSVP trực tuyến",
      body: "Khách trả lời trên trang của bạn và bạn dễ dàng biết ai sẽ đến.",
      videoAria: "Hoạt ảnh: RSVP trực tuyến",
    },
    {
      title: "Gửi thiệp mời",
      body: "Chỉ cần một liên kết—khách mở, đọc và mỉm cười.",
      videoAria: "Hoạt ảnh: gửi liên kết mời",
    },
    {
      title: "Một liên kết, nhiều ngôn ngữ",
      body: "Lưu một lần là mini site lên mạng, hiển thị đúng ngôn ngữ từng khách.",
      videoAria: "Hoạt ảnh: mini site đa ngôn ngữ",
    },
  ],
};

export const LANDING_TRANSLATIONS: Record<AppLocale, LandingCoreStrings> = {
  en,
  ar,
  "zh-Hans": zhHans,
  da,
  nl,
  fr,
  de,
  he,
  hi,
  id,
  it,
  ja,
  no,
  "pt-PT": ptPT,
  ru,
  sk,
  es,
  tr,
  uk,
  vi,
};

export function getLandingStrings(locale: AppLocale): LandingStrings {
  const core = LANDING_TRANSLATIONS[locale] ?? LANDING_TRANSLATIONS.en;
  const faq = LANDING_FAQ[locale] ?? LANDING_FAQ.en;
  return {
    ...core,
    faqTitle: faq.faqTitle,
    faqItems: faq.faqItems,
  };
}
